import { assign, fromPromise, setup } from 'xstate';
import { openDeleteConfirmation, showAxiosErrorAction } from '~/features/actions';
import {
  closeModal,
  deleteData,
  openModal,
  resetLoading,
  resetState,
  setContext,
  setCreateSuccess,
  setDataSort,
  setDataToDelete,
  setDataToEdit,
  setGetSuccess,
  setPatchSuccess,
  toggleLoading,
} from './actions';
import {
  Events as BaseEvents,
  DeleteEvent,
  GetEvent,
  PatchEvent,
  SendSharedContextEvent,
  SortEvent,
  SubmitEvent,
} from './events';
import { BaseFormContext, BaseFormScope, DataItem, SortItem } from './types';

interface BaseCrudMachineProps<TData extends DataItem, TForm, TSort extends SortItem> {
  initialForm: Partial<TForm>;
  sort: TSort;
  apiCalls: {
    get: (props: {
      input: BaseFormScope<BaseFormContext<TForm, TSort>, GetEvent>;
    }) => Promise<TData[]>;
    create: (props: {
      input: BaseFormScope<BaseFormContext<TForm, TSort>, SubmitEvent<TForm>>;
    }) => Promise<TData>;
    patch: (props: {
      input: BaseFormScope<BaseFormContext<TForm, TSort>, PatchEvent<TForm>>;
    }) => Promise<TData>;
    delete: (props: {
      input: BaseFormScope<BaseFormContext<TForm, TSort>, DeleteEvent>;
    }) => Promise<TData>;
  };
}

export const createBaseCrudMachine = <TData, TForm extends DataItem, TSort extends SortItem>(
  props: BaseCrudMachineProps<TData & DataItem, TForm, TSort>
) => {
  type Context = BaseFormContext<TForm, TSort>;
  type Events =
    | BaseEvents
    | SortEvent<string>
    | SubmitEvent<TForm>
    | PatchEvent<TForm>
    | GetEvent
    | SendSharedContextEvent<Context>;
  const { initialForm, sort, apiCalls } = props;
  return setup({
    actors: {
      getCall: fromPromise(apiCalls.get),
      createCall: fromPromise(apiCalls.create),
      updateCall: fromPromise(apiCalls.patch),
      deleteCall: fromPromise(apiCalls.delete),
    },
    types: {
      context: {} as BaseFormContext<TForm, TSort>,
      events: {} as Events,
      input: {} as Partial<Context>,
    },
    actions: {
      setContext: assign(setContext<Context>),
      toggleLoading: assign(toggleLoading<Context>),
      resetLoading: assign(resetLoading),
      openModal: assign(openModal),
      closeModal: assign(closeModal),
      setSort: assign(setDataSort<Context>),
      setGetSuccess: assign(setGetSuccess<Context>),
      setCreateSuccess: assign(setCreateSuccess<Context>),
      setPatchSuccess: assign(setPatchSuccess<Context>),
      setItemToEdit: assign(setDataToEdit<Context>),
      setItemToDelete: assign(setDataToDelete<Context>),
      deleteItemFromContext: assign(deleteData<Context>),
      resetState: assign(resetState<Context>(initialForm)),
      queueReload: assign({ pendingReload: true }),
      clearPendingReload: assign({ pendingReload: undefined }),
      sendError: showAxiosErrorAction,
      sendDeleteConfirmation: openDeleteConfirmation,
      log: () => null, // FIX: no-op outlet - remove when better logging paradigm is implemented
    },
  }).createMachine({
    context: (props) => ({
      loading: false,
      pagination: {
        page: 1,
        pageSize: 5,
        total: 0,
      },
      modal: {
        edit: false,
        create: false,
      },
      form: initialForm,
      data: props.input.data || [],
      idToDelete: -1,
      sort: sort,
      providerId: props.input.providerId || -1,
    }),
    initial: '$_VIEW',
    on: {
      ON_OPEN_MODAL: {
        actions: 'openModal',
        target: '.$_FORM',
      },
      ON_CLOSE_MODAL: {
        actions: ['closeModal', 'resetState'],
        target: '.$_VIEW',
      },
      ON_SEND_SHARED_CONTEXT: {
        actions: ['setContext'],
      },
    },
    states: {
      $_VIEW: {
        // [MCV-1753] A reload asked for while the modal form was open (a notification saying the
        // list changed under the user) runs once they are back here, instead of being dropped.
        always: {
          guard: ({ context }) => context.pendingReload === true,
          actions: ['clearPendingReload', 'toggleLoading'],
          target: '$_LOAD',
        },
        on: {
          ON_LOAD: {
            actions: ['log', 'toggleLoading'],
            target: `$_LOAD`,
          },
          ON_SORT_TABLE: {
            actions: ['setSort'],
            target: '$_LOAD',
          },
          ON_ADD: {
            actions: ['openModal'],
            target: '$_FORM',
          },
          ON_EDIT: {
            actions: ['openModal', 'setItemToEdit'],
            target: '$_FORM',
          },
          ON_DELETE: {
            actions: ['setItemToDelete'],
            target: '$_SEND_DELETE',
          },
          ON_SEND_DELETE_CONFIRMATION: {
            actions: 'sendDeleteConfirmation',
          },
        },
      },
      $_FORM: {
        on: {
          // Not while a fetch or a save is running: those drop it, as they always have, so the
          // pages that ask twice on entry still fetch once. Only the open form queues it.
          ON_LOAD: { actions: 'queueReload' },
          ON_SUBMIT: { target: '$_SEND_CREATE' },
          ON_UPDATE: { target: '$_SEND_UPDATE' },
        },
      },
      $_SEND_CREATE: {
        invoke: {
          id: 'create',
          src: 'createCall',
          input: ({ context, event }) =>
            ({ context, event }) as { context: Context; event: SubmitEvent<TForm> },
          onDone: {
            target: '$_VIEW',
            actions: ['setCreateSuccess', 'resetState'],
          },
          onError: {
            target: '$_VIEW',
            actions: ['sendError', 'resetLoading'],
          },
        },
      },
      $_SEND_UPDATE: {
        invoke: {
          id: 'update',
          src: 'updateCall',
          input: ({ context, event }) =>
            ({ context, event }) as { context: Context; event: PatchEvent<TForm> },
          onDone: {
            target: '$_VIEW',
            actions: ['setPatchSuccess', 'resetState'],
          },
          onError: {
            target: '$_FORM',
            actions: ['sendError', 'resetLoading'],
          },
        },
      },
      $_SEND_DELETE: {
        invoke: {
          id: 'delete',
          src: 'deleteCall',
          input: ({ context, event }) =>
            ({ context, event }) as { context: Context; event: DeleteEvent },
          onDone: {
            target: '$_VIEW',
            actions: ['deleteItemFromContext', 'resetLoading'],
          },
          onError: {
            target: '$_FORM',
            actions: ['sendError', 'resetLoading'],
          },
        },
      },
      $_LOAD: {
        invoke: {
          id: 'get',
          src: 'getCall',
          input: ({ context, event }) =>
            ({ context, event }) as { context: Context; event: GetEvent },
          onDone: {
            target: '$_VIEW',
            actions: ['setGetSuccess', 'resetState'],
          },
          onError: {
            target: '$_VIEW',
            actions: ['sendError', 'resetState'],
          },
        },
      },
    },
  });
};
